/**
 * The drinking window: four optional years, and what they say about a wine
 * today.
 *
 * This is the one thing the spreadsheet cannot do at all, and the reason the
 * application exists — so it is also the only loud element in the interface.
 * The band a screen draws is computed here rather than in a component, because
 * the same evaluation answers the drinking-window filter, the monthly report's
 * lists, and the suggestion to move a bottle from the ageing cabinet to the
 * serving one. Three readers, one rule.
 *
 * A single from–to pair cannot express "drinkable in 2028, at its best
 * 2032–2036, gone by 2042", which is the shape of the actual judgement — hence
 * four years rather than two, each of them optional, because a wine may be
 * known only vaguely.
 */
/** Years outside this are a typo — 20211 rather than 2021 — not a judgement. */
const EARLIEST = 1800;
const LATEST = 2200;
function present(years) {
    return [years.drinkFrom, years.peakFrom, years.peakTo, years.drinkBy].filter((year) => year !== null);
}
/**
 * Whether the four years can be true at once. A peak that ends before it
 * begins is a data error rather than an unusual wine, and the write path
 * should refuse it — which is why this is exported rather than kept private:
 * the form validates with it, and the band declines to draw when it fails.
 */
export function windowYearsAreCoherent(years) {
    const ordered = present(years);
    if (ordered.some((y) => !Number.isInteger(y) || y < EARLIEST || y > LATEST)) {
        return false;
    }
    // The four are given in the order they must occur; equal is allowed, because
    // a peak may be one year long.
    return ordered.every((year, i) => i === 0 || year >= (ordered[i - 1] ?? year));
}
function stateAt(years, year) {
    if (years.drinkBy !== null && year > years.drinkBy)
        return "past";
    if (years.drinkFrom !== null && year < years.drinkFrom)
        return "young";
    const afterPeakStart = years.peakFrom === null || year >= years.peakFrom;
    const beforePeakEnd = years.peakTo === null || year <= years.peakTo;
    if ((years.peakFrom !== null || years.peakTo !== null) &&
        afterPeakStart &&
        beforePeakEnd) {
        return "peak";
    }
    return "ready";
}
/**
 * What to say about this wine this year. `unknown` where no year is recorded
 * or the years contradict each other — the interface says so rather than
 * guessing, because a machine-made guess presented as a judgement is exactly
 * what the provenance fields exist to prevent.
 */
export function evaluateDrinkingWindow(years, currentYear) {
    if (present(years).length === 0 || !windowYearsAreCoherent(years))
        return "unknown";
    return stateAt(years, currentYear);
}
/**
 * The band to draw: the years it spans, where now falls on it, and the
 * coloured stretches between. Returns null when there is nothing honest to
 * draw, so a row with no window renders as a row with no band rather than as
 * an invented one.
 *
 * The span always contains the current year, so *now* is never off the end of
 * its own band — a wine twenty years from its window still shows how far away
 * it is.
 */
export function drinkingWindowBand(years, currentYear) {
    const known = present(years);
    if (known.length === 0 || !windowYearsAreCoherent(years))
        return null;
    if (!Number.isInteger(currentYear))
        return null;
    let firstYear = Math.min(...known, currentYear);
    let lastYear = Math.max(...known, currentYear);
    if (firstYear === lastYear) {
        firstYear -= 1;
        lastYear += 1;
    }
    const span = lastYear - firstYear;
    const at = (year) => (year - firstYear) / span;
    const boundaries = [...new Set([firstYear, ...known, lastYear])].sort((a, b) => a - b);
    const segments = [];
    for (let i = 0; i < boundaries.length - 1; i += 1) {
        const fromYear = boundaries[i] ?? firstYear;
        const toYear = boundaries[i + 1] ?? lastYear;
        // Classified on the midpoint, so a boundary year belongs to exactly one
        // stretch instead of to both of the stretches that meet at it.
        const state = stateAt(years, (fromYear + toYear) / 2);
        const previous = segments[segments.length - 1];
        if (previous && previous.state === state) {
            segments[segments.length - 1] = { ...previous, toYear, end: at(toYear) };
        }
        else {
            segments.push({ state, fromYear, toYear, start: at(fromYear), end: at(toYear) });
        }
    }
    return {
        state: stateAt(years, currentYear),
        firstYear,
        lastYear,
        nowAt: at(currentYear),
        segments,
    };
}
//# sourceMappingURL=drinking-window.js.map